import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../shared/layout/Header';
import { transactionService } from '../../api';
import './TransactionsPage.css';

const STAGES = [
  ['COMMISSION', 'کمیسیون'],
  ['DOCUMENTS', 'مدارک'],
  ['PROMISE_NOTE', 'قولنامه'],
  ['NOTARY', 'دفترخانه'],
  ['HANDOVER', 'تحویل'],
  ['POST_TRANSACTION', 'پشتیبانی'],
];

const TASK_STAGE = {
  PAY_COMMISSION: 'COMMISSION', MAKE_PAYMENT: 'COMMISSION', CONFIRM_PAYMENT: 'COMMISSION',
  UPLOAD_DOCUMENT: 'DOCUMENTS', REVIEW_DOCUMENT: 'DOCUMENTS',
  PREPARE_PROMISE_NOTE: 'PROMISE_NOTE', SIGN_PROMISE_NOTE: 'PROMISE_NOTE',
  SEND_TO_NOTARY: 'NOTARY', REVIEW_AT_NOTARY: 'NOTARY', ATTEND_NOTARY: 'NOTARY',
  SCHEDULE_HANDOVER: 'HANDOVER', COMPLETE_HANDOVER: 'HANDOVER',
  SUBMIT_FEEDBACK: 'POST_TRANSACTION', RESOLVE_ISSUE: 'POST_TRANSACTION',
};

const STATUS = {
  BLOCKED: 'در انتظار مرحله قبلی', PENDING: 'آماده اقدام', IN_PROGRESS: 'در حال انجام',
  COMPLETED: 'تکمیل شده', FAILED: 'ناموفق', CANCELLED: 'لغو شده', WAIVED: 'صرف‌نظر شده',
};

const formatDate = (value) => value
  ? new Date(value).toLocaleString('fa-IR', { dateStyle: 'medium', timeStyle: 'short' })
  : null;

const formatMoney = (value) => value
  ? `${Number(value).toLocaleString('fa-IR')} تومان`
  : null;

function Progress({ progress }) {
  const percent = progress?.percent || 0;
  return (
    <div className="tx-progress" aria-label={`${percent} درصد پیشرفت`}>
      <div className="tx-progress-bar"><span style={{ width: `${percent}%` }} /></div>
      <span>{percent}٪</span>
    </div>
  );
}

function TaskCard({ task, onStart, busy }) {
  const dependencies = task.dependencies?.length || 0;
  return (
    <article className={`tx-task tx-task-${task.status.toLowerCase()}`}>
      <div className="tx-task-heading">
        <div>
          <span className="tx-task-sequence">مرحله {task.sequence}</span>
          <h3>{task.title}</h3>
        </div>
        <span className="tx-status">{STATUS[task.status] || task.status}</span>
      </div>
      {task.description && <p>{task.description}</p>}
      <div className="tx-task-meta">
        <span>مسئول: {task.assigned_role}</span>
        {task.due_at && <span>مهلت: {formatDate(task.due_at)}</span>}
        {dependencies > 0 && <span>{dependencies} پیش‌نیاز</span>}
      </div>
      {task.status === 'BLOCKED' && (
        <div className="tx-waiting">این مرحله پس از تکمیل پیش‌نیازهای آن فعال می‌شود.</div>
      )}
      {task.can_start && (
        <button className="tx-primary-button" disabled={busy} onClick={() => onStart(task)}>
          {busy ? 'در حال ثبت...' : 'شروع این مرحله'}
        </button>
      )}
      {task.status === 'IN_PROGRESS' && task.can_complete && (
        <div className="tx-domain-note">
          ادامه این مرحله از طریق عملیات اختصاصی آن، مانند پرداخت امن، بارگذاری مدرک یا امضای قولنامه انجام می‌شود.
        </div>
      )}
      {task.events?.length > 0 && (
        <details className="tx-events">
          <summary>تاریخچه ({task.events.length})</summary>
          {[...task.events].reverse().map(event => (
            <div key={event.id}>
              <span>{formatDate(event.created_at)}</span>
              <span>{event.comment || `${event.previous_status || '—'} ← ${event.new_status}`}</span>
            </div>
          ))}
        </details>
      )}
    </article>
  );
}

function TransactionList({ transactions, onOpen }) {
  if (!transactions.length) return <div className="tx-empty">هنوز پرونده معامله‌ای ندارید.</div>;
  return (
    <div className="tx-grid">
      {transactions.map(transaction => (
        <button className="tx-card" key={transaction.id} onClick={() => onOpen(transaction.id)}>
          <div className="tx-card-top">
            <strong>{transaction.listing_info?.address || `آگهی #${transaction.listing}`}</strong>
            <span>{transaction.status === 'COMPLETED' ? 'تکمیل شده' : 'فعال'}</span>
          </div>
          <div className="tx-card-info">
            {transaction.my_role === 'BUYER' ? 'شما خریدار هستید' : 'شما مالک هستید'}
            {' · '}{transaction.listing_info?.listing_type === 'RENT' ? 'اجاره' : 'فروش'}
          </div>
          <Progress progress={transaction.progress} />
          <div className="tx-card-footer">
            <span>{transaction.actionable_task_count || 0} اقدام برای شما</span>
            <span>مشاهده پرونده ←</span>
          </div>
        </button>
      ))}
    </div>
  );
}

export default function TransactionsPage() {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(transactionId ? null : []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyTask, setBusyTask] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(transactionId
        ? await transactionService.fetchTransaction(transactionId)
        : await transactionService.fetchTransactions());
    } catch (err) {
      if (err?.response?.status === 401) navigate('/login', { replace: true });
      else setError(err?.response?.data?.detail || 'دریافت اطلاعات معامله ناموفق بود.');
    } finally {
      setLoading(false);
    }
  }, [navigate, transactionId]);

  useEffect(() => { load(); }, [load]);

  const groupedTasks = useMemo(() => {
    const groups = Object.fromEntries(STAGES.map(([key]) => [key, []]));
    for (const task of data?.tasks || []) groups[TASK_STAGE[task.task_type] || data.stage]?.push(task);
    return groups;
  }, [data]);

  const startTask = async (task) => {
    setBusyTask(task.id);
    setError('');
    try {
      await transactionService.performTaskAction(task.id, 'start');
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'شروع مرحله ناموفق بود.');
    } finally {
      setBusyTask(null);
    }
  };

  return (
    <div className="tx-page" dir="rtl">
      <Header />
      <main className="tx-shell">
        <div className="tx-page-heading">
          <div>
            {transactionId && <button className="tx-back" onClick={() => navigate('/transactions')}>بازگشت به معاملات</button>}
            <h1>{transactionId ? 'پرونده معامله' : 'معاملات من'}</h1>
            <p>{transactionId ? 'مراحل، مسئولیت‌ها و سوابق این معامله' : 'پیگیری معاملات پذیرفته‌شده و اقدامات بعدی'}</p>
          </div>
          <button className="tx-refresh" onClick={load} disabled={loading}>به‌روزرسانی</button>
        </div>

        {loading && <div className="tx-empty">در حال بارگذاری...</div>}
        {error && <div className="tx-error">{error}</div>}
        {!loading && !error && !transactionId && <TransactionList transactions={data} onOpen={id => navigate(`/transactions/${id}`)} />}

        {!loading && data && transactionId && (
          <>
            <section className="tx-summary">
              <div>
                <span className="tx-eyebrow">{data.listing_info?.listing_type === 'RENT' ? 'معامله اجاره' : 'معامله فروش'}</span>
                <h2>{data.listing_info?.address || `آگهی #${data.listing}`}</h2>
                <p>{data.my_role === 'BUYER' ? `مالک: ${data.owner_info?.full_name}` : `خریدار: ${data.buyer_info?.full_name}`}</p>
                {data.accepted_revision && (
                  <div className="tx-deal-terms">
                    {data.accepted_revision.asking_price && <span>قیمت توافقی: {formatMoney(data.accepted_revision.asking_price)}</span>}
                    {data.accepted_revision.deposit && <span>ودیعه: {formatMoney(data.accepted_revision.deposit)}</span>}
                    {data.accepted_revision.monthly_rent && <span>اجاره: {formatMoney(data.accepted_revision.monthly_rent)}</span>}
                    {data.accepted_revision.delivery_date && <span>تحویل: {data.accepted_revision.delivery_date}</span>}
                  </div>
                )}
              </div>
              <div className="tx-summary-progress">
                <strong>{data.actionable_task_count || 0} اقدام برای شما</strong>
                <Progress progress={data.progress} />
              </div>
            </section>

            <nav className="tx-stage-nav" aria-label="مراحل معامله">
              {STAGES.map(([key, label]) => (
                <a key={key} href={`#stage-${key}`} className={data.current_stage === key ? 'active' : ''}>{label}</a>
              ))}
            </nav>

            {STAGES.map(([key, label]) => groupedTasks[key]?.length > 0 && (
              <section className="tx-stage" id={`stage-${key}`} key={key}>
                <h2>{label}</h2>
                <div className="tx-task-list">
                  {groupedTasks[key].map(task => (
                    <TaskCard key={task.id} task={task} onStart={startTask} busy={busyTask === task.id} />
                  ))}
                </div>
              </section>
            ))}

            {!data.tasks?.length && (
              <div className="tx-error">برای این معامله هنوز گردش‌کار فعالی تعریف نشده است. مدیر سامانه باید الگوی معامله را تنظیم کند.</div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
